import { buildExperiencesPassportFromOffers } from 'api/mappers/experienceMap'
import { fetchPassportExperienceTicketsById } from 'actions/ExperienceActions'
import useExperienceList from 'hooks/Experiences/useExperienceList'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { useOfferTagsForOffers } from 'hooks/Offers/useOfferTags'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import { isEligibleExperiencesPassportTicket } from 'lib/checkout/experiences/cart'
import { useContext, useEffect, useMemo } from 'react'
import type { ExperiencesPassport, ExperiencesPassportCheckoutSource, ExperiencesPassportOfferSource } from './experiencesPassportTypes'
import { OFFER_TYPE_BED_BANK } from 'constants/offer'

export interface ExperiencesPassportOfferResult {
  passport?: ExperiencesPassport
  checkoutSources: Array<ExperiencesPassportCheckoutSource>
  fetching: boolean
}

export interface ExperiencesPassportOfferInput {
  key: string
  offer: ExperiencesPassportOfferSource
}

interface Options {
  offer?: ExperiencesPassportOfferSource
  filters?: {
    checkIn?: string
    checkOut?: string
  }
  shouldFetch?: boolean
}

interface BatchOptions {
  offers: Array<ExperiencesPassportOfferInput>
  filters?: Options['filters']
  shouldFetch?: boolean
}

const EMPTY_RESULT: ExperiencesPassportOfferResult = {
  checkoutSources: [],
  fetching: false,
}

const PASSPORT_TAG_OPTIONS = {
  tagType: 'passport' as const,
  includeAssignments: true,
  includedAssignmentEntityTypeIn: ['EXPERIENCE' as const],
}

function isBedbankOffer(offer: ExperiencesPassportOfferSource): offer is App.BedbankOffer {
  return offer.type === OFFER_TYPE_BED_BANK
}

function getPassportExperienceIds(tags: Array<App.OfferTag>): Array<string> {
  return Array.from(new Set(tags.flatMap(tag =>
    tag.assignments
      ?.filter(assignment => assignment.entityType === 'EXPERIENCE')
      .map(assignment => assignment.entityId) ?? [],
  )))
}

function getOfferIdentity(offer: ExperiencesPassportOfferSource): string {
  return `${offer.type}:${offer.id}`
}

/**
 * Resolves Passport eligibility for several offer sources with shared tag,
 * experience and ticket requests. Results remain keyed by the caller's key so
 * two rooms that happen to use the same offer stay independently addressable.
 */
export function useExperiencesPassportForOffers({
  offers,
  filters,
  shouldFetch = true,
}: BatchOptions): { byKey: Record<string, ExperiencesPassportOfferResult>, fetching: boolean } {
  const dispatch = useAppDispatch()
  const currentCurrency = useAppSelector(state => state.geo.currentCurrency)
  const passportTickets = useAppSelector(state => state.experience.passportTickets)
  const fetchingPassportTickets = useAppSelector(state => state.experience.fetchingPassportTickets)
  const passportTicketErrors = useAppSelector(state => state.experience.passportTicketErrors)

  const uniqueOffers = useMemo(() => Array.from(
    new Map(offers.map(input => [getOfferIdentity(input.offer), input.offer])).values(),
  ), [offers])
  const offerIds = useMemo(() => uniqueOffers.map(offer => offer.id), [uniqueOffers])
  const { tagsByOfferId, fetchingByOfferId } = useOfferTagsForOffers(
    shouldFetch ? offerIds : [],
    PASSPORT_TAG_OPTIONS,
    filters,
  )
  const passportExperienceIdsByOffer = useMemo(() => Object.fromEntries(uniqueOffers.map(offer => [
    getOfferIdentity(offer),
    shouldFetch ? getPassportExperienceIds(tagsByOfferId[offer.id] ?? []) : [],
  ])), [shouldFetch, tagsByOfferId, uniqueOffers])
  const passportExperienceIds = useMemo(() => Array.from(new Set(
    Object.values(passportExperienceIdsByOffer).flat(),
  )), [passportExperienceIdsByOffer])
  const fetchingTags = uniqueOffers.some(offer => fetchingByOfferId[offer.id])
  const shouldFetchExperiences = shouldFetch && !fetchingTags && passportExperienceIds.length > 0

  const [experienceList, experiences] = useExperienceList({
    experienceIds: shouldFetchExperiences ? passportExperienceIds : undefined,
    showUnlisted: true,
    limit: passportExperienceIds.length || 1,
    from: filters?.checkIn,
    to: filters?.checkOut,
  })
  const passportExperiencesById = useMemo(() => Object.fromEntries(
    experiences.filter(experience => experience.isExperiencePassport).map(experience => [experience.id, experience]),
  ), [experiences])

  useEffect(() => {
    if (shouldFetchExperiences) {
      dispatch(fetchPassportExperienceTicketsById(...passportExperienceIds))
    }
  }, [dispatch, passportExperienceIds, shouldFetchExperiences])

  const byKey = useMemo(() => Object.fromEntries(offers.map(({ key, offer }) => {
    const experienceIds = passportExperienceIdsByOffer[getOfferIdentity(offer)] ?? []
    const passportExperiences = experienceIds.flatMap(id => {
      const experience = passportExperiencesById[id]
      return experience ? [experience] : []
    })
    const ticketsReady = experienceIds.length >= passportExperiences.length && passportExperiences.every(experience =>
      passportTickets[experience.id]?.some(isEligibleExperiencesPassportTicket),
    )
    const checkoutSources = ticketsReady ? passportExperiences.map(experience => ({
      experience,
      tickets: passportTickets[experience.id] ?? [],
    })) : []
    const offerWithAddress = {
      ...offer,
      ...(isBedbankOffer(offer) && { locationHeading: offer.property?.address?.city }),
    }
    const passport = shouldFetchExperiences && passportExperiences.length > 0 && ticketsReady ?
        buildExperiencesPassportFromOffers(offerWithAddress, passportExperiences, currentCurrency) : undefined
    const fetchingTickets = experienceIds.some(id =>
      fetchingPassportTickets[id] || (!(id in passportTickets) && !passportTicketErrors[id]),
    )
    const fetching = experienceIds.length > 0 && (
      fetchingByOfferId[offer.id] || fetchingTickets || !!experienceList.fetching
    )

    return [key, { passport, checkoutSources, fetching }]
  })), [currentCurrency, experienceList.fetching, fetchingByOfferId, fetchingPassportTickets, offers, passportExperienceIdsByOffer, passportExperiencesById, passportTicketErrors, passportTickets, shouldFetchExperiences])

  return {
    byKey,
    fetching: Object.values(byKey).some(result => result.fetching),
  }
}

function useExperiencesPassportForOffer({ offer, filters, shouldFetch = true }: Options = {}): ExperiencesPassportOfferResult {
  const { offer: offerPageOffer, bedbankOffer, checkInDate, checkOutDate } = useContext(OfferPageStateContext)
  // An SPP rate can belong to a different LPC offer than the offer that opened
  // the page. An explicit source must therefore win over the page context.
  const resolvedOffer = offer ?? offerPageOffer ?? bedbankOffer
  const inputs = useMemo(() => resolvedOffer ? [{
    key: getOfferIdentity(resolvedOffer),
    offer: resolvedOffer,
  }] : [], [resolvedOffer])
  const { byKey } = useExperiencesPassportForOffers({
    offers: inputs,
    filters: {
      checkIn: filters?.checkIn ?? checkInDate,
      checkOut: filters?.checkOut ?? checkOutDate,
    },
    shouldFetch,
  })

  return resolvedOffer ? byKey[getOfferIdentity(resolvedOffer)] ?? EMPTY_RESULT : EMPTY_RESULT
}

export default useExperiencesPassportForOffer
